import { z } from 'zod';

/**
 * Entidades do módulo Catálogo (= CatalogModule no monolith). Espelha os DTOs
 * de `ProductAdminController` e `CatalogAdminController`. Schemas Zod + tipos
 * inferidos para os recursos; interfaces para os inputs de criação/edição.
 */

/** Resultado paginado padrão do back (`PaginatedResult<T>`). */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export interface PaginationInput {
  page?: number;
  perPage?: number;
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

export type ProductStatus = 'missing_config' | 'configured' | 'archived';

export const PRODUCT_STATUSES: ProductStatus[] = [
  'missing_config',
  'configured',
  'archived',
];

export const ProductStatusSchema = z.enum([
  'missing_config',
  'configured',
  'archived',
]);

/** Gateways de onde um entregável pode vir. Espelha o `CatalogProvider` do back. */
export const CATALOG_PROVIDERS = ['guru', 'hotmart'] as const;
export const CatalogProviderSchema = z.enum(CATALOG_PROVIDERS);
export type CatalogProvider = z.infer<typeof CatalogProviderSchema>;

/**
 * O que a compra entrega é decidido pelo **par** produto × oferta, escopado pela
 * conta de gateway — duas ofertas do mesmo produto (6 meses / 6 meses + bônus)
 * são entregáveis diferentes. As quatro partes são `null` enquanto o produto é
 * rascunho (`missing_config`) e viram string quando alguém cadastra.
 */
export const ProductSchema = z.object({
  id: z.string(),
  provider: CatalogProviderSchema.nullable(),
  providerAccountId: z.string().nullable(),
  productExternalId: z.string().nullable(),
  offerExternalId: z.string().nullable(),
  name: z.string(),
  status: ProductStatusSchema,
});

export type Product = z.infer<typeof ProductSchema>;

/** As quatro partes da chave, como o formulário as envia — todas obrigatórias. */
export interface ProductExternalKeyInput {
  provider: CatalogProvider;
  providerAccountId: string;
  productExternalId: string;
  offerExternalId: string;
}

export interface CreateProductInput extends ProductExternalKeyInput {
  name: string;
}

export interface UpdateProductStatusInput {
  status: ProductStatus;
}

export type UpdateProductExternalKeyInput = ProductExternalKeyInput;

// ---------------------------------------------------------------------------
// Product perks (associação perk → produto)
// ---------------------------------------------------------------------------

export type ProductPerkOverduePolicy = 'keep_active' | 'suspend';

export const PRODUCT_PERK_OVERDUE_POLICIES: ProductPerkOverduePolicy[] = [
  'keep_active',
  'suspend',
];

export const ProductPerkSchema = z.object({
  id: z.string(),
  productId: z.string(),
  perkId: z.string(),
  durationInMilliseconds: z.number().nullable(),
  maxUsages: z.number().nullable(),
  renewEveryInMilliseconds: z.number().nullable(),
  targetScopes: z.array(z.string()),
  overduePolicy: z.enum(['keep_active', 'suspend']),
  /**
   * Um ciclo pago deste produto **reentrega** este perk? `false` (default) =
   * entrega uma vez — todo ciclo do mesmo contrato é a mesma origem e do 2º em
   * diante vira no-op, que é o certo para curso parcelado. `true` = cada
   * pagamento que avança o contrato soma janela/saldo, que é o certo para
   * assinatura que renova.
   */
  deliversPerCycle: z.boolean(),
});

export type ProductPerk = z.infer<typeof ProductPerkSchema>;

/** Uma adição do lote — o que era o corpo do `POST .../perks` de item único. */
export interface ProductPerkBatchAddItem {
  perkId: string;
  durationInMilliseconds?: number;
  maxUsages?: number;
  renewEveryInMilliseconds?: number;
  targetScopes?: string[];
  overduePolicy?: ProductPerkOverduePolicy;
  /** Ausente = `false` (entrega uma vez). Ver `ProductPerkSchema`. */
  deliversPerCycle?: boolean;
}

/**
 * O diff inteiro do produto: `remove` são ids de `ProductPerk`. "Ou tudo, ou
 * nada" — em caso de erro, 422 com um `PerkAssociationBatchError` por índice.
 */
export interface ApplyProductPerkBatchInput {
  add: ProductPerkBatchAddItem[];
  remove: string[];
}

export interface ProductPerkBatchResult {
  added: ProductPerk[];
  removed: ProductPerk[];
}

// ---------------------------------------------------------------------------
// Perks (catálogo)
// ---------------------------------------------------------------------------

export type PerkType = 'role' | 'feature';

export const PERK_TYPES: PerkType[] = ['role', 'feature'];

export const PerkTypeSchema = z.enum(['role', 'feature']);

export type PerkGrantPolicyKind =
  | 'time_limited_credit'
  | 'usage_limited_credit'
  | 'time_bounded_recurring_credit'
  | 'time_bounded_unlimited_usage'
  | 'permanent';

export const PERK_GRANT_POLICY_KINDS: PerkGrantPolicyKind[] = [
  'time_limited_credit',
  'usage_limited_credit',
  'time_bounded_recurring_credit',
  'time_bounded_unlimited_usage',
  'permanent',
];

export const PerkGrantPolicyKindSchema = z.enum([
  'time_limited_credit',
  'usage_limited_credit',
  'time_bounded_recurring_credit',
  'time_bounded_unlimited_usage',
  'permanent',
]);

/**
 * Os valores da concessão, em termos **relativos** (duração, não `expiresAt`).
 * Quem concede é que os informa — o perk só diz a *forma* (`grantPolicyKind`).
 * Espelha `GrantPolicyParams` no back.
 */
export interface GrantPolicyParams {
  durationInMilliseconds?: number;
  maxUsages?: number;
  renewEveryInMilliseconds?: number;
}

export type GrantPolicyParamField = keyof GrantPolicyParams;

export type FieldRequirement = 'required' | 'forbidden';

/**
 * `GET /admin/catalog/perks/grant-policy-kinds`: quais params cada kind exige na
 * concessão. É a fonte-da-verdade do back publicada — nenhuma tela deve manter
 * uma cópia desta tabela.
 */
export const GrantPolicySpecSchema = z.object({
  kind: PerkGrantPolicyKindSchema,
  params: z.object({
    durationInMilliseconds: z.enum(['required', 'forbidden']),
    maxUsages: z.enum(['required', 'forbidden']),
    renewEveryInMilliseconds: z.enum(['required', 'forbidden']),
  }),
  /** Janela de tempo: conceder de novo **estende** o pacote existente. */
  isWindow: z.boolean(),
});

export type GrantPolicySpec = z.infer<typeof GrantPolicySpecSchema>;

/**
 * Uma **forma de concessão** da capacidade: o perk propriamente dito. É o
 * `perkId` daqui que vai no corpo do grant — não se concede uma role/feature,
 * concede-se um perk.
 */
export const PerkGrantFormSchema = z.object({
  perkId: z.string(),
  grantPolicyKind: PerkGrantPolicyKindSchema,
  scopeIds: z.array(z.string()),
  grantableByPerkKeys: z.array(z.string()),
  grantsPerkKeys: z.array(z.string()),
  requiresPerkKeys: z.array(z.string()),
  /**
   * `!= null` = forma **depreciada**: não é mais concedível (quem tem mantém).
   * Sai da oferta do editor; a linha já atribuída continua, com badge.
   */
  deprecatedAt: z.string().nullable(),
});

export type PerkGrantForm = z.infer<typeof PerkGrantFormSchema>;

/**
 * `GET /admin/catalog/perks`: uma linha por **capacidade** `(type, key)`, com um
 * item em `grantForms` por kind existente. Sem valores — eles são de quem
 * concede. `name`/`description` vêm da definição (role/feature) e são `null`
 * quando o perk é órfão.
 */
export const PerkGroupSchema = z.object({
  key: z.string(),
  type: PerkTypeSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  /** `!= null` = a **capacidade** foi depreciada (da definição). `null` em órfão/ativa. */
  deprecatedAt: z.string().nullable(),
  grantForms: z.array(PerkGrantFormSchema),
});

export type PerkGroup = z.infer<typeof PerkGroupSchema>;

// ---------------------------------------------------------------------------
// Portadores de perk (contagem + listagem) — a tela `Catálogo → Perks`
// ---------------------------------------------------------------------------

/**
 * Um grupo para a contagem: a `key` da capacidade (só serve para casar a
 * resposta com a linha) e **todos** os `perkId` dela (uma capacidade tem um
 * `perkId` por `grantPolicyKind`). Espelha `POST /admin/perks/counts`.
 */
export interface PerkHolderCountGroup {
  key: string;
  perkIds: string[];
}

/** Portadores distintos e ativos de uma linha. `count` é por profile, não por grant. */
export interface PerkHolderCount {
  key: string;
  count: number;
}

/**
 * Um portador já hidratado para exibição no modal "ver usuários". O delivery
 * devolve só `platformProfileId`; o identity resolve o resto
 * (`POST /admin/identity/platform-profiles/resolve`). `email` é `null` quando o
 * `userId` não tem conta.
 */
export const ResolvedPlatformProfileSchema = z.object({
  platformProfileId: z.string(),
  userId: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  scopeId: z.string(),
});

export type ResolvedPlatformProfile = z.infer<
  typeof ResolvedPlatformProfileSchema
>;

// ---------------------------------------------------------------------------
// Perk presets (lista nomeada de perks já configurados)
// ---------------------------------------------------------------------------

/**
 * Preset = atalho de UI, **não** origem de concessão. Ele não é concedível e não
 * entra em `@Roles(...)`: é a lista das permissões que um time recebe, não a
 * permissão em si. Ao ser aplicado, os itens são **clonados** — mexer no preset
 * depois não altera nada já concedido.
 */
export const PerkPresetSchema = z.object({
  id: z.string(),
  /**
   * Chave estável (`{plataforma}.{presetCamelCase}`) pela qual o **back**
   * referencia o preset — é por ela que um cadastro sabe qual pacote aplicar.
   * Trocá-la aqui muda o que aquele fluxo encontra, então não é rótulo.
   */
  key: z.string(),
  name: z.string(),
  description: z.string(),
});

export type PerkPreset = z.infer<typeof PerkPresetSchema>;

/** Um perk do preset, já com os valores que o kind dele exige. */
export const PerkPresetItemSchema = z.object({
  id: z.string(),
  presetId: z.string(),
  perkId: z.string(),
  durationInMilliseconds: z.number().nullable(),
  maxUsages: z.number().nullable(),
  renewEveryInMilliseconds: z.number().nullable(),
  targetScopes: z.array(z.string()),
});

export type PerkPresetItem = z.infer<typeof PerkPresetItemSchema>;

export interface CreatePerkPresetInput {
  key: string;
  name: string;
  description: string;
}

/** Uma adição do lote — o item do preset, com os valores que o kind exige. */
export interface PerkPresetItemBatchAddItem {
  perkId: string;
  durationInMilliseconds?: number;
  maxUsages?: number;
  renewEveryInMilliseconds?: number;
  targetScopes?: string[];
}

/** O diff inteiro do preset: `remove` são ids de `PerkPresetItem`. */
export interface ApplyPerkPresetItemBatchInput {
  add: PerkPresetItemBatchAddItem[];
  remove: string[];
}

export interface PerkPresetItemBatchResult {
  added: PerkPresetItem[];
  removed: PerkPresetItem[];
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const RoleSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  // Role e feature são igualmente multi-scope, com o mesmo endpoint de update
  // (`PUT .../roles/:id/scopes`). ⚠️ Tirar um scope revoga os grants de quem
  // está nele.
  scopeIds: z.array(z.string()),
  /** `!= null` = depreciada (não concede a gente nova; a aba a exibe com badge). */
  deprecatedAt: z.string().nullable(),
});

export type CatalogRole = z.infer<typeof RoleSchema>;

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

export const FeatureSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  scopeIds: z.array(z.string()),
  /** `!= null` = depreciada (ver `RoleSchema`). */
  deprecatedAt: z.string().nullable(),
});

export type Feature = z.infer<typeof FeatureSchema>;

// ---------------------------------------------------------------------------
// Shared: atualizar scopes (roles + features)
// ---------------------------------------------------------------------------

export interface UpdateScopesInput {
  scopeIds: string[];
}
